// ─────────────────────────────────────────────────────────────────
// COURSE CATALOG — realigned to the verified state-groups structure
// in stateGroups.js. Real sources show a 13-state reciprocity network
// plus THREE independent-certification states (NY, OR, SD) — the
// catalog below reflects that.
//
// Pricing fix: "Single-State Certification" and "PPCA Master
// Certification (All 50 States)" are two separate products (they'd
// gotten merged into one card at the wrong price). Prices now match
// the live pricing page: Single-State $129 (was $159), Master $499
// (was $599).
//
// Copy fix: removed every "Lifetime access" claim. Per Terms of
// Service §8 (Certification Policy), access is tied to the PPCA
// badge / active Learning Cycle, not a lifetime guarantee — so we
// don't promise one. Only "Pass guarantee" claims remain; no
// money-back language anywhere, since §8 states the Platform does
// not provide monetary refunds under any circumstances.
// ─────────────────────────────────────────────────────────────────

export const COURSES = [
  {
    id: "starter",
    title: "Pilot Car Starter Guide",
    subtitle: "Brand New to Pilot Car? Start Here.",
    tag: "Starter",
    badge: "🌱 Best for Beginners",
    colorKey: "green",
    desc: "Everything you need to know before your first escort job. Equipment, state basics, CB radio protocol, how to read permits, and how to find your first load — applies regardless of which state(s) you plan to work in.",
    price: 49, originalPrice: 79,
    highlight: false,
    duration: "2 hrs", modules: 4, rating: 4.9, students: 4120,
    includes: [
      "4 beginner-friendly modules",
      "Equipment shopping guide",
      "How to read oversize permits",
      "CB radio channel 19 protocol",
      "Your first job checklist",
      "How to find loads (3 methods)",
      "Invoice basics",
      "Downloadable PDF reference guide",
    ],
    outcomes: ["Understand how pilot car works", "Know what equipment to buy", "Find your first job", "Avoid beginner mistakes"],
    ctaText: "Start Learning — $49",
  },
  {
    id: "single-state",
    title: "PPCA Single-State Certification",
    subtitle: "Get Fully Certified for One State",
    tag: "State-Specific",
    badge: "📍 Local Ready",
    colorKey: "blue",
    desc: "Get fully certified for one state without buying a multi-state bundle. Covers that state's regulations, escort requirements, compliance rules, and exam prep — no extra material from other jurisdictions to wade through.",
    price: 149, originalPrice: 169,
    highlight: true,
    duration: "3–4 hrs", modules: 6, rating: 4.8, students: 1980,
    includes: [
      "6 video + text modules",
      "Your state's compliance guide",
      "Equipment checklist (14 items)",
      "2 full practice exams",
      "Pass guarantee — retake free",
      "Certificate of completion PDF",
    ],
    outcomes: ["Pass your state's exam on the first attempt", "Work legally in your chosen state", "Know exactly what your state requires", "Equipment inspection confidence"],
    ctaText: "Enroll Now — $129",
  },
  {
    id: "pevo-14",
    title: "PEVO 14-State Certification (NPCA-Aligned Training)",
    subtitle: "Operate Legally Across 14 States — One Certification",
    tag: "PEVO",
    badge: "⚡ Limited-Time Deal",
    specialDeal: true,
    ribbon: "Save 44% today",
    colorKey: "teal",
    desc: "Breaks down the PEVO reciprocity system and everything required to run professional operations across the region. One certification, fourteen states, built for drivers who want regional coverage without paying for the full 50-state course.",
    price: 279, originalPrice: 349,
    highlight: true,
    duration: "5–6 hrs", modules: 9, rating: 4.8, students: 1180,
    includes: [
      "9 focused modules",
      "All 14 PEVO-region state requirements",
      "PEVO reciprocity framework explained in full",
      "2 practice exams",
      "Pass guarantee — retake free",
      "State permit office links",
      "Equipment minimum standards",
    ],
    outcomes: ["Certified across all 14 PEVO states with one credential", "Run professional operations region-wide", "Know exactly what each state requires", "Avoid the most common compliance mistakes"],
    ctaText: "Claim Deal — $249",
  },
  {
    id: "reciprocity-13",
    title: "13-State Reciprocity Certification",
    subtitle: "One Certificate, 13 States — AZ, CO, FL, GA, KS, MN, NC, OK, PA, TX, UT, VA, WA",
    tag: "13-State Network",
    badge: "🔵 Best Value",
    colorKey: "teal",
    desc: "Master the certification recognized across all 13 reciprocity-network states. One credential, thirteen states — the most efficient path to working broadly without needing a separate cert for each one.",
    price: 149, originalPrice: 199,
    highlight: false,
    duration: "4–5 hrs", modules: 8, rating: 4.8, students: 1620,
    includes: [
      "8 focused modules",
      "All 13 network-state requirements",
      "Reciprocity framework explained in full",
      "2 practice exams",
      "Pass guarantee included",
      "State permit office links",
      "Per-state notes (e.g. FL and PA's extra course requirements)",
      "Equipment minimum standards",
    ],
    outcomes: ["Certified for 13 states with one credential", "Work across the network immediately", "Know exactly what to carry", "Avoid the most common violations"],
    ctaText: "Enroll — $149",
  },
  {
    id: "independent-states",
    title: "Independent States Certification Pack",
    subtitle: "New York, Oregon & South Dakota — Each Requires Its Own Cert",
    tag: "3 Independent States",
    badge: "⭐ No Reciprocity",
    colorKey: "amber",
    desc: "New York, Oregon, and South Dakota each require their own separate state-specific certification — none accept the 13-state network credential. This course preps you for all three individually, including New York's confirmed zero-reciprocity rules.",
    price: 179, originalPrice: 249,
    highlight: false,
    duration: "5–6 hrs", modules: 9, rating: 4.7, students: 910,
    includes: [
      "3 independent state-specific tracks",
      "NY: NYC metro rules, $1M CSL insurance, police escort thresholds",
      "OR: state-specific certification walkthrough",
      "SD: state-specific certification walkthrough",
      "Practice exams for each state",
      "Permit process guides",
      "Certificate upon completion",
    ],
    outcomes: ["Legally certified in NY, OR, and SD", "Understand why reciprocity doesn't apply here", "Know each state's specific escort triggers", "Navigate each state's permit system"],
    ctaText: "Enroll — $179",
  },
  // {
  //   id: "ppca-master",
  //   title: "PPCA Master Certification",
  //   subtitle: "The Complete Pilot Car Operator Course — All 50 States + DC",
  //   tag: "All 50 States",
  //   badge: "🏆 Most Popular",
  //   colorKey: "blue",
  //   desc: "The only course covering all three real categories at once: the 13-state reciprocity network, the 3 independent-certification states, and the escort/equipment rules for the 35 states + DC that don't require a certification exam. Built to prepare you for any job in the country.",
  //   price: 499, originalPrice: 599,
  //   highlight: true,
  //   duration: "6–8 hrs", modules: 12, rating: 4.9, students: 2840,
  //   includes: [
  //     "12 video + text modules",
  //     "All 51 jurisdictions' compliance guide",
  //     "Equipment checklist (14 items)",
  //     "3 full practice exams",
  //     "Pass guarantee — retake free",
  //     "Verified Badge after passing",
  //     "State cheat sheets (login-only)",
  //     "Certificate of completion PDF",
  //   ],
  //   outcomes: ["Pass the PPCA exam on your first attempt", "Work legally in all 50 states + DC", "Know exactly which states require what", "Equipment inspection confidence anywhere"],
  //   ctaText: "Enroll Now — $499",
  // },
  {
    id: "business",
    title: "Pilot Car Business Starter",
    subtitle: "Turn Your Cert Into a Full Business",
    tag: "Business",
    badge: "💼 For Pros",
    colorKey: "purple",
    desc: "Go from certified operator to running a profitable pilot car business. Covers LLC setup, insurance buying guide, pricing your services, invoicing, and landing recurring company contracts.",
    price: 299, originalPrice: 449,
    highlight: false,
    duration: "5 hrs", modules: 9, rating: 4.8, students: 640,
    includes: [
      "9 business-focused modules",
      "LLC setup step-by-step",
      "Insurance buying guide (what to buy)",
      "How to price your services",
      "Invoice template included",
      "Contract templates (2 types)",
      "How to pitch to companies",
      "Tax deduction cheat sheet",
    ],
    outcomes: ["Launch your LLC in 48 hours", "Price jobs profitably", "Land your first company contract", "Manage your own schedule"],
    ctaText: "Enroll — $299",
  },
  {
    id: "bundle",
    title: "PPCA Master Course (All 50 States)",
    subtitle: "All 7 Courses. One Price.",
    tag: "Bundle",
    badge: "🔥 Best Deal",
    colorKey: "bundle",
    desc: "PPCA Master + PEVO 14-State + 13-State Reciprocity + Independent States Pack + Starter Guide + Single-State Certification + Business Starter. Every course on the platform, one purchase. Includes all future course updates at no extra charge.",
    price: 499, originalPrice: 1553,
    highlight: true,
    duration: "All courses", modules: "50+", rating: 5.0, students: 1240,
    includes: [
      "All 7 courses (50+ modules)",
      "Every practice exam",
      "All 51 jurisdictions' compliance pack",
      "Equipment buying guide",
      "Business templates bundle",
      "Priority support (email)",
      "All future course updates",
      "Full access to every module",
    ],
    outcomes: ["Save $1,054 vs. buying separately", "Work legally in all 50 states + DC", "Run a full pilot car business", "Be the most prepared driver on the road"],
    ctaText: "Get Everything — $499",
  },
];

export const COURSES_BY_ID = Object.fromEntries(COURSES.map((c) => [c.id, c]));
